// Shared style + colour tokens for the Foundation Lesson onboarding
// flow. Extracted from the original FoundationLesson.jsx so per-screen
// modules can share one source of truth without re-importing the whole
// orchestrator.

// Fallback for `var(--gold, ...)`. The brand's "gold" accent is
// actually rose — so the fallback matches the --gold variable in
// variables.css (#A8003F). Previously this was a literal gold
// (#B8923E), which would flash if the CSS variable failed to load
// on first paint, breaking the "no yellow anywhere" palette rule.
export const GOLD_FALLBACK = '#A8003F';

// Movement accent colors from the Foundation Lesson spec:
// SQUEEZE = rose (--gold #A8003F), RELAX = mauve (#7A5A6A),
// PUSH = slate (#4a6a80). The "gold" name is historical — the brand's
// --gold token is actually rose. RELAX / PUSH match the --relax /
// --push tokens in src/css/variables.css.
export const SPEC_COLORS = {
  squeeze: `var(--gold, ${GOLD_FALLBACK})`,
  relax: 'var(--relax, #7A5A6A)',
  push: 'var(--push, #4a6a80)',
};

export const s = {
  screen: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    background: 'var(--cream, #FAF6F1)',
    display: 'flex',
    flexDirection: 'column',
  },
  progressTrack: {
    flexShrink: 0,
    height: 3,
    width: '100%',
    background: 'var(--beige, #E8DDD0)',
    marginTop: 'var(--safe-top, env(safe-area-inset-top, 0px))',
  },
  progressFill: {
    height: '100%',
    background: `var(--gold, ${GOLD_FALLBACK})`,
    transition: 'width 300ms ease',
  },
  scroll: {
    flex: 1,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: '32px 24px 16px',
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    width: '100%',
    maxWidth: 460,
    margin: '0 auto',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  eyebrow: {
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'var(--warm-brown, #8B7355)',
    margin: '0 0 12px',
  },
  title: {
    fontFamily: "var(--font-title, 'Playfair Display', 'Cormorant Garamond', Georgia, serif)",
    fontSize: 32,
    fontWeight: 500,
    fontStyle: 'italic',
    color: 'var(--dark-brown, #5A3E2B)',
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
    margin: '8px 0 16px',
  },
  subtitle: {
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 18,
    fontWeight: 400,
    color: 'var(--warm-brown, #8B7355)',
    lineHeight: 1.7,
    marginBottom: 24,
  },
  body: {
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 18,
    fontWeight: 400,
    color: 'var(--text, #3D2B1F)',
    lineHeight: 1.7,
    marginBottom: 24,
  },
  callout: {
    background: 'var(--nude, #F5EDE3)',
    border: `1.5px solid var(--gold, ${GOLD_FALLBACK})`,
    borderRadius: 'var(--radius-md, 12px)',
    padding: '16px 18px',
    marginBottom: 16,
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 16,
    lineHeight: 1.6,
    color: 'var(--dark-brown, #5A3E2B)',
  },
  calloutExtra: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: '1px solid var(--beige, #E8DDD0)',
    color: 'var(--warm-brown, #8B7355)',
    fontStyle: 'italic',
  },
  illustrationBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 0 24px',
  },
  tips: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 16,
  },
  tipRow: {
    display: 'flex',
    gap: 12,
    background: 'var(--nude, #F5EDE3)',
    border: '1.5px solid var(--beige, #E8DDD0)',
    borderRadius: 'var(--radius-md, 12px)',
    padding: '14px 16px',
  },
  tipDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
    marginTop: 8,
    background: `var(--gold, ${GOLD_FALLBACK})`,
  },
  tipText: {
    flex: 1,
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 16,
    lineHeight: 1.55,
    color: 'var(--dark-brown, #5A3E2B)',
  },
  // ── New bullet-card design system ────────────────────────────────────
  bulletStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 11,
    marginBottom: 20,
  },
  bulletCard: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    background: 'var(--surface, #FFFFFF)',
    border: '1px solid var(--border, #E8DDD4)',
    borderRadius: 12,
    padding: '14px 16px',
  },
  bulletIconDisc: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: 'var(--primary-pale, #F5D6E0)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bulletText: {
    flex: 1,
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 15,
    lineHeight: 1.55,
    color: 'var(--dark-brown, #5A3E2B)',
    paddingTop: 4,
  },
  signalSection: {
    background: 'var(--nude, #F5EDE3)',
    border: '1.5px solid var(--beige, #E8DDD0)',
    borderRadius: 'var(--radius-md, 12px)',
    padding: '16px 18px',
    marginBottom: 16,
  },
  signalLabel: {
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  signalList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  signalItem: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 16,
    lineHeight: 1.55,
    color: 'var(--dark-brown, #5A3E2B)',
  },
  signalBullet: {
    display: 'inline-block',
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0,
    marginTop: 9,
  },
  // Squeeze-tips lesson (screen 8): full 18px / 1.7 reading rhythm per
  // brand rules. Overrides the default bulletText (15px / 1.55) which
  // is tuned for shorter screens; here each tip is multi-sentence so
  // the extra height pays off.
  squeezeTipLine: {
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 18,
    lineHeight: 1.7,
    color: 'var(--dark-brown, #5A3E2B)',
  },
  // Push lesson (screen 10): numbered steps with a subtle left thread
  // connecting them. The thread carries the canal / elevator metaphor
  // visually so the reader's eye descends as they read.
  pushEyebrow: {
    display: 'inline-block',
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.26em',
    textTransform: 'uppercase',
    color: `var(--gold, ${GOLD_FALLBACK})`,
    marginBottom: 8,
  },
  pushStepsList: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    paddingLeft: 52,
    marginTop: 18,
  },
  pushStepsThread: {
    position: 'absolute',
    left: 18,
    top: 8,
    bottom: 8,
    width: 1,
    background: `linear-gradient(to bottom, color-mix(in srgb, var(--gold, ${GOLD_FALLBACK}) 32%, transparent) 0%, color-mix(in srgb, var(--gold, ${GOLD_FALLBACK}) 18%, transparent) 100%)`,
  },
  pushStep: {
    position: 'relative',
  },
  pushStepIndex: {
    position: 'absolute',
    left: -52,
    top: 0,
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'var(--cream, #FAF6F1)',
    border: `1.5px solid color-mix(in srgb, var(--gold, ${GOLD_FALLBACK}) 28%, var(--beige, #E8DDD0))`,
    fontFamily: "var(--font-title, 'Fraunces', 'Newsreader', Georgia, serif)",
    fontStyle: 'italic',
    fontSize: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: `var(--gold, ${GOLD_FALLBACK})`,
  },
  pushStepBody: {
    fontFamily: "var(--font-body, 'Newsreader', 'Fraunces', Georgia, serif)",
    fontSize: 18,
    lineHeight: 1.7,
    color: 'var(--dark-brown, #5A3E2B)',
    margin: 0,
  },
  // Marble-visualization step (step 4). No card border — just a soft
  // nude inset so the eye pauses without the block reading as a
  // formal card. The tiny caption below cues the "picture this"
  // moment without naming it.
  pushStepMarble: {
    background: 'var(--nude, #F5EDE3)',
    borderRadius: 'var(--radius-sm, 10px)',
    padding: '14px 16px 12px',
    marginLeft: -8,
    marginRight: -4,
  },
  pushStepMarbleCaption: {
    display: 'block',
    marginTop: 8,
    fontFamily: "var(--font-title, 'Fraunces', 'Newsreader', Georgia, serif)",
    fontStyle: 'italic',
    fontSize: 13,
    letterSpacing: '0.02em',
    color: `var(--gold, ${GOLD_FALLBACK})`,
  },
  // Level tick on the thread. Three of these land at roughly 10 / 50 / 90
  // percent of the steps list height — mirroring the canal's III / II / I
  // depth labels on the RingPlayer stage. Hairline width + tiny roman
  // keeps the thread reading as a vertical guide, not a chart axis.
  pushStepsTick: {
    position: 'absolute',
    left: 11,
    width: 14,
    height: 1,
    transform: 'translateY(-50%)',
    background: `color-mix(in srgb, var(--gold, ${GOLD_FALLBACK}) 42%, transparent)`,
    pointerEvents: 'none',
  },
  // Marble pinned to the thread at the step-4 y. Drops into place
  // (motion.css .pushMarble keyframe) when step 4 enters the viewport.
  // Size is smaller than the step-number medallion so it reads as an
  // object travelling the canal rather than a duplicate index.
  // Marble sits centered on the vertical thread. Thread is at list x=18
  // and step content starts at list paddingLeft 52, so from inside the
  // step coordinate system the thread lives at -34. top:50% centers the
  // marble on the step's midline so it reads as "arrived at level II".
  pushStepMarbleDot: {
    position: 'absolute',
    left: -34,
    top: '50%',
    filter: 'drop-shadow(0 2px 6px color-mix(in srgb, var(--gold, #A8003F) 42%, transparent))',
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
    zIndex: 2,
  },
  // Elevated variant of noteCard. Used on the Gentle Push "Why it matters"
  // card so the scientific-reassurance card sits a layer forward of the
  // warm-tint "Good to know" above it. Deeper shadow + cooler surface +
  // slightly thicker border separate them as peers, not twins.
  noteCardElevated: {
    background: 'color-mix(in srgb, var(--cream, #FAF6F1) 80%, #ffffff)',
    borderColor: 'color-mix(in srgb, var(--dark-brown, #5A3E2B) 12%, transparent)',
    borderWidth: 1.5,
    boxShadow: 'var(--shadow-elevated, 0 2px 8px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.10))',
    padding: '22px 18px 20px',
  },
  // Highlight-note card (ATTENTION + GOOD TO KNOW). Chip sits above
  // the body so the label reads as a mark, not a heading. Warm variant
  // is richer nude + gold chip for the GOOD TO KNOW "warm secret"
  // tone; default variant is the app's nude + warm-brown chip.
  noteCard: {
    background: 'var(--nude, #F5EDE3)',
    border: '1px solid var(--beige, #E8DDD0)',
    borderRadius: 'var(--radius-md, 12px)',
    padding: '20px 18px 18px',
    marginTop: 14,
  },
  noteCardWarm: {
    background: `color-mix(in srgb, var(--gold, ${GOLD_FALLBACK}) 12%, var(--nude, #F5EDE3))`,
    borderColor: `color-mix(in srgb, var(--gold, ${GOLD_FALLBACK}) 28%, var(--beige, #E8DDD0))`,
  },
  noteRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
  },
  noteIconDisc: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: 'var(--primary-pale, #F7E8EE)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  noteIconDiscWarm: {
    background: `color-mix(in srgb, var(--gold, ${GOLD_FALLBACK}) 14%, #ffffff)`,
  },
  noteChip: {
    display: 'inline-block',
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.26em',
    textTransform: 'uppercase',
    color: 'var(--warm-brown, #8B7355)',
    background: 'rgba(255,255,255,0.7)',
    padding: '4px 10px',
    borderRadius: 999,
  },
  noteChipWarm: {
    color: `var(--gold, ${GOLD_FALLBACK})`,
    background: 'rgba(255,255,255,0.85)',
  },
  noteTitle: {
    fontFamily: "var(--font-title, 'Fraunces', 'Newsreader', Georgia, serif)",
    fontStyle: 'italic',
    fontSize: 20,
    lineHeight: 1.25,
    color: 'var(--dark-brown, #5A3E2B)',
    margin: '0 0 8px',
    letterSpacing: '-0.01em',
  },
  noteBody: {
    fontFamily: "var(--font-body, 'Newsreader', 'Fraunces', Georgia, serif)",
    fontSize: 16,
    lineHeight: 1.65,
    color: 'var(--dark-brown, #5A3E2B)',
    margin: 0,
  },
  linkButton: {
    display: 'block',
    background: 'none',
    border: 'none',
    padding: '12px 0',
    width: '100%',
    textAlign: 'center',
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 15,
    fontWeight: 500,
    color: `var(--gold, ${GOLD_FALLBACK})`,
    textDecoration: 'underline',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    marginBottom: 8,
  },
  backButton: {
    display: 'block',
    background: 'none',
    border: 'none',
    padding: '12px 0',
    width: '100%',
    textAlign: 'center',
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 15,
    fontWeight: 500,
    color: 'var(--warm-brown, #8B7355)',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  footer: {
    flexShrink: 0,
    background: 'var(--cream, #FAF6F1)',
    padding: '12px 24px calc(16px + env(safe-area-inset-bottom, 0px))',
  },
  cta: {
    display: 'block',
    width: '100%',
    maxWidth: 460,
    margin: '0 auto',
    padding: '18px 16px',
    border: 'none',
    borderRadius: 'var(--radius-md, 12px)',
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: '0.02em',
    textAlign: 'center',
    transition: 'background 200ms ease, opacity 200ms ease',
    touchAction: 'manipulation',
  },
  ctaEnabled: {
    background: `var(--gold, ${GOLD_FALLBACK})`,
    color: '#fff',
    cursor: 'pointer',
    opacity: 1,
  },
  ctaDisabled: {
    background: 'var(--surface-high, #E8DDD0)',
    color: 'var(--text-secondary, #6B5A4A)',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  badgeRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 24,
    margin: '8px 0 28px',
  },
  badgeItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
  },
  badgeCircle: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: `var(--gold, ${GOLD_FALLBACK})`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(184, 146, 62, 0.25)',
  },
  badgeLabel: {
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'var(--warm-brown, #8B7355)',
  },
  // ── Felt-sense check (per-screen "How clear is that?" chips) ─────────
  // Shown above the footer CTA on every teaching screen. Optional —
  // skipping it never blocks Continue. Tapping a chip records a signal
  // into user_progress.data.fl_signals[screenId] for analytics.
  checkBlock: {
    marginTop: 24,
    paddingTop: 18,
    borderTop: '1px solid var(--beige, #E8DDD0)',
  },
  checkPrompt: {
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--warm-brown, #8B7355)',
    marginBottom: 10,
  },
  checkRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  checkChip: {
    flex: '1 1 0',
    minWidth: 0,
    padding: '10px 12px',
    border: '1px solid var(--border, #E8DDD4)',
    borderRadius: 999,
    background: 'var(--surface, #FFFFFF)',
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--dark-brown, #5A3E2B)',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    transition: 'background 150ms ease, border-color 150ms ease, color 150ms ease',
  },
  checkChipSelected: {
    background: `color-mix(in srgb, var(--gold, ${GOLD_FALLBACK}) 12%, var(--surface, #FFFFFF))`,
    borderColor: `var(--gold, ${GOLD_FALLBACK})`,
    color: `var(--gold, ${GOLD_FALLBACK})`,
  },
  checkHint: {
    marginTop: 10,
    fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
    fontSize: 13,
    lineHeight: 1.5,
    color: 'var(--warm-brown, #8B7355)',
    fontStyle: 'italic',
  },
};
